<template>
  <div class="d-flex posts-container">
    <!-- Sidebar: Channels -->
    <div class="channels-sidebar">
      <div class="mb-2 channels-filters">
        <div class="channels-search">
          <SearchBox
            v-model="channelSearch"
            constant-width
          />
        </div>
        <Tippy
          ref="channelGroupFilter"
          trigger="click"
          interactive
          theme="light"
          placement="bottom-start"
          max-width="260"
        >
          <template #trigger>
            <div class="sort-option-selected d-flex align-items-center">
              <div class="flex-grow-1">
                <div
                  class="font-weight-bold text-secondary"
                  style="font-size: 10px; min-width: 180px"
                >
                  {{ $t('groups.group') }}
                </div>
                <div class="small pr-2 text-truncate">
                  {{ selectedChannelGroupFilterName }}
                </div>
              </div>
              <i class="far fa-chevron-down text-secondary" />
            </div>
          </template>

          <div style="min-width: 240px; max-height: 360px; overflow: auto">
            <div
              class="sort-option text-left d-flex align-items-center"
              :class="{ active: selectedChannelGroupFilter === 'all' }"
              @click="setChannelGroupFilter('all')"
            >
              {{ $t('company.all') }}
            </div>
            <div
              v-for="group in catalogChannelGroups"
              :key="group.catalogId"
              class="sort-option text-left d-flex align-items-center"
              :class="{ active: selectedChannelGroupFilter === group.catalogId }"
              @click="setChannelGroupFilter(group.catalogId)"
            >
              <i :class="`${getCatalogTypeIcon(group.productCategory)} mr-2`" />
              <span class="flex-grow-1 text-truncate">{{ group.catalogName }}</span>
            </div>
          </div>
        </Tippy>
      </div>

      <div class="sidebar-header">
        {{ $t('posts.channels') }}
      </div>

      <!-- Group 1: Public channel -->
      <div
        v-if="showPublicChannel"
        class="channel-item"
        :class="{ active: selectedChannelIds.includes('public') }"
        :title="$t('posts.publicChannel')"
        @click="toggleChannelSelection('public')"
      >
        <i class="far fa-globe mr-2" />
        {{ $t('posts.publicChannel') }}
      </div>

      <hr class="my-2">

      <!-- System announcements: only in the publisher organisation -->
      <template v-if="systemChannelId && showPublicChannel">
        <div class="channel-group-title">
          {{ $t('news.systemGroup') }}
        </div>
        <div
          class="channel-item"
          :class="{ active: selectedChannelIds.includes(systemChannelId) }"
          @click="toggleChannelSelection(systemChannelId)"
        >
          <i class="far fa-bullhorn mr-2" />
          <div style="min-width: 0">
            <div>{{ $t('news.systemChannel') }}</div>
            <div class="small text-secondary">
              {{ $t('news.systemChannelHint') }}
            </div>
          </div>
        </div>
        <hr class="my-2">
      </template>

      <!-- Following groups: catalog + offers -->
      <div
        v-for="group in filteredCatalogChannelGroups"
        :key="group.catalogId"
        class="channel-group"
      >
        <div
          class="channel-group-title"
          :class="{ active: selectedChannelIds.includes(group.catalogId) }"
          @click="toggleGroupSelection(group)"
        >
          {{ group.catalogName }}
        </div>

        <div
          v-for="ch in group.channels"
          :key="ch.id"
          class="channel-item"
          :class="{ active: selectedChannelIds.includes(ch.id) }"
          :title="ch.name"
          @click="toggleChannelSelection(ch.id)"
        >
          <i :class="`${getCatalogTypeIcon(group.productCategory)} mr-2`" />
          <span class="text-truncate">{{ ch.name }}</span>
        </div>

        <hr class="my-2">
      </div>
    </div>

    <!-- Main content: Posts list + form -->
    <div
      ref="postsContent"
      class="posts-content flex-grow-1"
      @scroll.passive="onPostsScroll"
    >
      <div
        v-if="loadingPosts"
        class="text-center py-4"
      >
        <Loader />
      </div>

      <template v-else>
        <div
          v-if="!showForm"
          class="d-flex justify-content-between align-items-center mb-3"
        >
          <button
            class="btn btn-sm btn-primary"
            style="border-radius: 20px"
            @click="startNewPost"
          >
            <i class="fas fa-plus mr-1" />
            {{ $t('posts.newPost') }}
          </button>
        </div>

        <!-- Post form (create/edit) -->
        <PostForm
          v-if="showForm"
          :key="formKey"
          :post="editingPost"
          :channel-id="selectedChannelId"
          :channel-groups="catalogChannelGroups"
          :system-channel-id="systemChannelId"
          class="mb-3"
          @saved="onPostSaved"
          @cancel="cancelForm"
        />

        <template v-else>
          <!-- Posts list -->
          <div
            v-if="sortedPosts.length === 0 && !showForm"
            class="text-center text-muted py-4"
          >
            {{ $t('posts.noPosts') }}
          </div>
          <div
            v-for="post in sortedPosts"
            :key="post.id"
            class="card mb-2 post-card"
          >
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div
                  class="d-flex flex-grow-1"
                  style="min-width: 0"
                >
                  <div
                    class="flex-grow-1"
                    style="min-width: 0"
                  >
                    <div class="post-title">
                      {{ post.title }}
                    </div>
                    <div class="post-meta mt-1">
                      <div class="post-date">
                        {{ formatDate(post.timestamp) }}
                      </div>
                      <span class="post-channel-badge">
                        {{ getPostChannelName(post.channelId) }}
                      </span>
                      <template v-if="post.announcement">
                        <span class="news-topic-label">
                          {{ $t(`news.topics.${post.announcement.topic}`) }}
                        </span>
                        <span :class="`badge badge-pill news-category news-category-${post.announcement.category}`">
                          {{ $t(`news.categories.${post.announcement.category}`) }}
                        </span>
                        <span class="post-channel-badge">
                          {{ announcementStatus(post) }}
                        </span>
                        <span
                          v-if="post.announcement.popup"
                          class="post-channel-badge"
                        >
                          {{ $t('news.popupBadge') }}
                        </span>
                        <span
                          v-if="post.announcement.pinned"
                          class="post-channel-badge"
                        >
                          <i class="far fa-thumbtack" />
                        </span>
                        <span
                          v-if="post.creatorEmail"
                          class="post-date"
                        >
                          {{ $t('news.publishedBy', { email: post.creatorEmail }) }}
                        </span>
                      </template>
                      <span
                        v-if="post.views && !post.announcement"
                        class="post-view-stats"
                        :title="viewStatsTooltip(post)"
                      >
                        <span class="post-view-stat">
                          <i class="far fa-eye" />
                          {{ post.views.opens }}
                        </span>
                        <span class="post-view-stat">
                          <i class="far fa-user" />
                          {{ post.views.uniqueOpens }}
                        </span>
                        <span class="post-view-stat">
                          <i class="far fa-bullhorn" />
                          {{ post.views.uniqueImpressions }}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="d-flex ml-2">
                  <div
                    class="secondary-icon mr-1"
                    @click="editPost(post)"
                  >
                    <i class="far fa-pen" />
                  </div>
                  <div
                    class="secondary-icon"
                    @click="deletePost(post)"
                  >
                    <i class="far fa-trash" />
                  </div>
                </div>
              </div>

              <div
                v-if="post.announcement && post.announcement.organizations"
                class="post-view-stats mt-1"
              >
                <span>{{ $t('news.stats.organizations') }}</span>
                <span class="post-view-stat">
                  <span class="news-dot news-dot-opened" />
                  {{ $t('news.stats.opened', { count: post.announcement.organizations.opened }) }}
                </span>
                <span class="post-view-stat">
                  <span class="news-dot news-dot-dismissed" />
                  {{ $t('news.stats.dismissed', { count: post.announcement.organizations.dismissed }) }}
                </span>
                <span class="post-view-stat">
                  <span class="news-dot" />
                  {{ $t('news.stats.notOpened', { count: post.announcement.organizations.notOpened }) }}
                </span>
                <span
                  v-if="helpfulTotal(post) > 0"
                  class="post-view-stat"
                >
                  {{ $t('news.stats.helpful', { percent: helpfulPercent(post), count: helpfulTotal(post) }) }}
                </span>
              </div>

              <div
                class="post-body mt-2"
                v-html="sanitizeHtml(post.body)"
              />

              <div
                v-if="imageMedia(post).length > 0"
                class="post-gallery mt-3"
              >
                <a
                  v-for="(m, idx) in imageMedia(post)"
                  :key="'img-' + idx"
                  :href="m.url"
                  target="_blank"
                  class="gallery-item"
                >
                  <img
                    :src="m.url"
                    class="gallery-image"
                  >
                </a>
              </div>

              <div
                v-if="fileMedia(post).length > 0"
                class="post-files mt-2"
              >
                <a
                  v-for="(m, idx) in fileMedia(post)"
                  :key="'file-' + idx"
                  :href="m.url"
                  target="_blank"
                  class="file-attachment"
                  :title="fileExtension(m.path)"
                >
                  <i :class="fileIcon(m.path)" />
                </a>
              </div>
            </div>
          </div>

          <div
            v-if="loadingMorePosts"
            class="text-center py-3"
          >
            <Loader size="20px" />
          </div>
        </template>
      </template>
    </div>

    <!-- Delete confirmation modal -->
    <BModal
      v-model="showDeleteConfirm"
      hide-footer
      :title="$t('general.confirmRemove')"
    >
      <p>{{ $t('posts.deleteConfirm') }}</p>
      <div class="text-right mt-3">
        <button
          class="btn btn-secondary mr-2"
          @click="showDeleteConfirm = false"
        >
          {{ $t('general.cancel') }}
        </button>
        <button
          class="btn btn-danger"
          @click="confirmDeletePost"
        >
          {{ $t('general.remove') }}
        </button>
      </div>
    </BModal>
  </div>
</template>

<script>
import axios from '@/api/axios';
import PostForm from '@/components/messages/PostForm';
import createSearch from '@/utils/search';
import sanitizeHtml from '@/utils/sanitizeHtml';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'Posts',
  data: () => ({
    selectedChannelIds: [],
    catalogChannelGroups: [],
    selectedChannelGroupFilter: 'all',
    channelSearch: '',
    allChannelIds: [],
    posts: [],
    loadingPosts: false,
    loadingMorePosts: false,
    nextCursor: '',
    postsPageSize: 10,
    showForm: false,
    editingPost: null,
    formKey: 0,

    // Delete confirmation
    showDeleteConfirm: false,
    postToDelete: null,
    channelNamesById: {},
    systemChannelId: null,
  }),
  components: {
    PostForm,
  },
  computed: {
    ...mapGetters(['payeeId']),
    ...mapGetters('shop', ['catalogs']),
    filteredCatalogChannelGroups() {
      const groups = this.selectedChannelGroupFilter === 'all'
        ? this.catalogChannelGroups
        : this.catalogChannelGroups.filter((group) => group.catalogId === this.selectedChannelGroupFilter);

      if (!this.channelSearch) {
        return groups;
      }

      const search = createSearch(this.channelSearch);

      return groups
        .map((group) => {
          const groupMatches = search(group.catalogName);
          if (groupMatches) {
            return group;
          }

          const channels = group.channels.filter((channel) => search(channel.name));
          if (channels.length === 0) {
            return null;
          }

          return {
            ...group,
            channels,
          };
        })
        .filter(Boolean);
    },
    selectedChannelGroupFilterName() {
      if (this.selectedChannelGroupFilter === 'all') {
        return this.$t('company.all');
      }

      const selectedGroup = this.catalogChannelGroups.find(
        (group) => group.catalogId === this.selectedChannelGroupFilter,
      );
      return (selectedGroup && selectedGroup.catalogName) || this.$t('company.all');
    },
    showPublicChannel() {
      if (this.selectedChannelGroupFilter !== 'all') {
        return false;
      }

      if (!this.channelSearch) {
        return true;
      }

      const search = createSearch(this.channelSearch);
      return search(this.$t('posts.publicChannel'));
    },
    selectedChannelId() {
      const selectedId = this.selectedChannelIds[0];

      if (!selectedId) {
        return 'public';
      }

      const selectedGroup = this.catalogChannelGroups.find((group) => group.catalogId === selectedId);
      if (selectedGroup && Array.isArray(selectedGroup.channels) && selectedGroup.channels.length > 0) {
        return selectedGroup.channels[0].id;
      }

      return selectedId;
    },
    sortedPosts() {
      return this.posts
        .slice()
        .sort((a, b) => moment(b.timestamp).unix() - moment(a.timestamp).unix());
    },
  },
  methods: {
    sanitizeHtml,
    announcementStatus(post) {
      if (post.announcement.draft) {
        return this.$t('news.status.draft');
      }
      return moment(post.timestamp).isAfter(moment())
        ? this.$t('news.status.scheduled')
        : this.$t('news.status.published');
    },
    helpfulTotal(post) {
      const o = post.announcement.organizations;
      return o.helpful + o.notHelpful;
    },
    helpfulPercent(post) {
      const total = this.helpfulTotal(post);
      return total === 0 ? 0 : Math.round((post.announcement.organizations.helpful / total) * 100);
    },
    async loadPostsConfig() {
      try {
        const { data } = await axios.get('messages/posts/config');
        this.systemChannelId = data.systemNewsPublisher ? data.systemChannelId : null;
      } catch {
        this.systemChannelId = null;
      }
    },
    ...mapActions('shop', [
      'getShopCatalogs',
      'getCatalogListings',
    ]),
    isImage(path) {
      if (!path) return false;
      return /\.(jpe?g|png|gif|webp|svg|bmp)$/i.test(path);
    },
    postThumbnailUrl(post) {
      if (post.thumbnailUrl) return post.thumbnailUrl;
      if (post.thumbnail && post.thumbnail.url) return post.thumbnail.url;
      return null;
    },
    imageMedia(post) {
      if (!post.media) return [];
      return post.media.filter((m) => this.isImage(m.path));
    },
    fileMedia(post) {
      if (!post.media) return [];
      return post.media.filter((m) => !this.isImage(m.path));
    },
    fileExtension(path) {
      if (!path) return '';
      return path.split('.').pop().toUpperCase();
    },
    fileIcon(path) {
      if (!path) return 'far fa-file';
      const ext = path.split('.').pop().toLowerCase();
      const map = {
        pdf: 'far fa-file-pdf',
        doc: 'far fa-file-word',
        docx: 'far fa-file-word',
        xls: 'far fa-file-excel',
        xlsx: 'far fa-file-excel',
        ppt: 'far fa-file-powerpoint',
        pptx: 'far fa-file-powerpoint',
        zip: 'far fa-file-archive',
        rar: 'far fa-file-archive',
        '7z': 'far fa-file-archive',
        gz: 'far fa-file-archive',
        mp3: 'far fa-file-audio',
        wav: 'far fa-file-audio',
        ogg: 'far fa-file-audio',
        mp4: 'far fa-file-video',
        avi: 'far fa-file-video',
        mov: 'far fa-file-video',
        mkv: 'far fa-file-video',
        txt: 'far fa-file-alt',
        csv: 'far fa-file-csv',
        js: 'far fa-file-code',
        html: 'far fa-file-code',
        css: 'far fa-file-code',
        json: 'far fa-file-code',
        xml: 'far fa-file-code',
      };
      return map[ext] || 'far fa-file';
    },
    formatDate(d) {
      const m = moment(d);
      if (m.year() === moment().year()) {
        return m.format('HH:mm D MMMM');
      }
      return m.format('HH:mm D MMMM YYYY');
    },
    getChannelId(catalogId, listingId) {
      return `${catalogId}_${listingId}`;
    },
    async loadChannelGroups() {
      const allChannelIds = ['public'];
      const catalogs = Array.isArray(this.catalogs) ? this.catalogs : [];
      const channelNamesById = {
        public: this.$t('posts.publicChannel'),
      };

      if (this.systemChannelId) {
        allChannelIds.push(this.systemChannelId);
        channelNamesById[this.systemChannelId] = this.$t('news.systemChannel');
      }

      const listingsPerCatalog = await Promise.all(
        catalogs.map(async (catalog) => {
          try {
            const { data } = await this.getCatalogListings({
              params: {
                catalogId: catalog.id,
              },
            });

            return {
              catalogId: catalog.id,
              listings: Array.isArray(data) ? data : [],
            };
          } catch {
            return {
              catalogId: catalog.id,
              listings: [],
            };
          }
        }),
      );

      const catalogChannelGroups = listingsPerCatalog
        .map(({ catalogId, listings }) => {
          const catalog = catalogs.find((c) => c.id === catalogId);
          const channels = listings.map((listing) => {
            const channelId = this.getChannelId(catalogId, listing.id);

            allChannelIds.push(channelId);
            channelNamesById[channelId] = listing.name;

            return {
              id: channelId,
              name: listing.name,
            };
          });

          return {
            catalogId,
            catalogName: (catalog && catalog.name) || '',
            productCategory: catalog && catalog.productCategory,
            channels,
          };
        })
        .filter((group) => group.channels.length > 0);

      this.catalogChannelGroups = catalogChannelGroups;
      this.allChannelIds = Array.from(new Set(allChannelIds));

      catalogs.forEach((catalog) => {
        if (catalog && catalog.id) {
          channelNamesById[catalog.id] = catalog.name || '';
        }
      });

      this.channelNamesById = channelNamesById;

      return this.allChannelIds;
    },
    async getQueryChannelIds() {
      if (this.allChannelIds.length === 0) {
        await this.loadChannelGroups();
      }

      if (this.selectedChannelIds.length === 0) {
        return this.allChannelIds;
      }

      const selectedId = this.selectedChannelIds[0];
      const selectedGroup = this.catalogChannelGroups.find((group) => group.catalogId === selectedId);

      if (selectedGroup) {
        return selectedGroup.channels.map((channel) => channel.id);
      }

      return [selectedId];
    },
    toggleChannelSelection(channelId) {
      this.toggleSingleSelection(channelId);
    },
    setChannelGroupFilter(groupId) {
      this.selectedChannelGroupFilter = groupId;
      if (this.$refs.channelGroupFilter && this.$refs.channelGroupFilter.tippy) {
        this.$refs.channelGroupFilter.tippy().hide();
      }
    },
    getCatalogTypeIcon(productCategory) {
      switch (productCategory) {
        case 'Catering':
          return 'fas fa-fork-knife';
        case 'Courses':
          return 'fas fa-baseball-bat-ball';
        case 'Events':
          return 'fas fa-people-roof';
        case 'Trips':
          return 'fas fa-campground';
        case 'Items':
          return 'fas fa-tag';
        default:
          return 'fas fa-baseball-bat-ball';
      }
    },
    toggleGroupSelection(group) {
      if (!group || !group.catalogId) {
        return;
      }

      this.toggleSingleSelection(group.catalogId);
    },
    toggleSingleSelection(selectionId) {
      if (this.selectedChannelIds[0] === selectionId) {
        this.selectedChannelIds = [];
      } else {
        this.selectedChannelIds = [selectionId];
      }

      this.cancelForm();
      this.requestPosts();
    },
    viewStatsTooltip(post) {
      if (!post.views || post.views.opens === 0) {
        return this.$t('posts.noViews');
      }

      const opens = this.$t('posts.opensTooltip', {
        opens: post.views.opens,
        users: post.views.uniqueOpens,
      });
      const reach = this.$t('posts.reachTooltip', {
        count: post.views.uniqueImpressions,
      });

      return `${opens}. ${reach}.`;
    },
    getPostChannelName(channelId) {
      if (!channelId) return '';
      if (channelId === 'public') {
        return this.$t('posts.publicChannel');
      }

      if (this.channelNamesById[channelId]) {
        return this.channelNamesById[channelId];
      }

      return channelId;
    },
    async requestPosts() {
      this.loadingPosts = true;
      this.posts = [];
      this.nextCursor = '';

      try {
        const channelIds = await this.getQueryChannelIds();

        const { data } = await axios.get('messages/posts', {
          params: {
            query: {
              channelIds,
              take: this.postsPageSize,
            },
          },
        });

        if (Array.isArray(data)) {
          this.posts = data;
          this.nextCursor = '';
          return;
        }

        this.posts = data.list || data.posts || [];
        this.nextCursor = data.cursor || '';
      } catch {
        this.posts = [];
        this.nextCursor = '';
      } finally {
        this.loadingPosts = false;
      }
    },
    async loadMorePosts() {
      if (!this.nextCursor || this.loadingPosts || this.loadingMorePosts || this.showForm) {
        return;
      }

      this.loadingMorePosts = true;

      try {
        const channelIds = await this.getQueryChannelIds();

        const { data } = await axios.get('messages/posts', {
          params: {
            query: {
              channelIds,
              cursor: this.nextCursor,
              take: this.postsPageSize,
            },
          },
        });

        const nextList = Array.isArray(data)
          ? data
          : (data.list || data.posts || []);

        const merged = [...this.posts, ...nextList];
        this.posts = Array.from(new Map(merged.map((post) => [post.id, post])).values());

        this.nextCursor = Array.isArray(data)
          ? ''
          : (data.cursor || '');
      } catch {
        // keep current list on pagination error
      } finally {
        this.loadingMorePosts = false;
      }
    },
    onPostsScroll(event) {
      const target = event && event.target;
      if (!target || this.loadingPosts || this.loadingMorePosts) {
        return;
      }

      const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (distanceToBottom <= 120) {
        this.loadMorePosts();
      }
    },
    startNewPost() {
      this.editingPost = null;
      this.formKey += 1;
      this.showForm = true;
    },
    editPost(post) {
      this.editingPost = post;
      this.formKey += 1;
      this.showForm = true;
    },
    cancelForm() {
      this.showForm = false;
      this.editingPost = null;
    },
    onPostSaved() {
      this.cancelForm();
      this.requestPosts();
    },
    deletePost(post) {
      this.postToDelete = post;
      this.showDeleteConfirm = true;
    },
    async confirmDeletePost() {
      const post = this.postToDelete;
      this.showDeleteConfirm = false;
      this.postToDelete = null;
      if (!post) return;
      try {
        await axios.delete('messages/posts', {
          params: {
            query: {
              id: post.id,
              timestamp: post.timestamp,
              channelId: post.channelId,
            },
          },
        });
        this.posts = this.posts.filter((p) => p.id !== post.id);
      } catch {
        // delete error
      }
    },
  },
  created() {
    this.$emit('page', 'posts');
    Promise.all([this.getShopCatalogs(), this.loadPostsConfig()])
      .finally(() => {
        this.loadChannelGroups()
          .finally(() => {
            this.requestPosts();
          });
      });
  },
};
</script>

<style lang="scss" scoped>
@import '~@/components/payers/style.scss';
@import '~@/styles/vars';

.news-topic-label {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 6px;
  border: 1px solid #cfd4da;
  background: #fff;
  color: #495057;
  font-size: 11px;
  font-weight: 500;
}

.news-category {
  font-size: 11px;
  color: #fff;
}

.news-category-New { background: $blue; }
.news-category-Improvement { background: $green; }
.news-category-Change { background: $orange; color: #212529; }
.news-category-Important { background: $red; }

.news-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ced4da;
  margin-right: 3px;
}

.news-dot-opened { background: $green; }
.news-dot-dismissed { background: $orange; }

.posts-container {
  height: calc(100vh - 100px);
  padding-bottom: 24px;
  box-sizing: border-box;
}

.channels-sidebar {
  width: 360px;
  min-width: 360px;
  border-right: 1px solid #eee;
  padding-right: 15px;
  overflow-y: auto;
}

.channels-filters {
  display: flex;
  align-items: center;
  gap: 8px;
}

.channels-search {
  flex: 1 1 auto;
  min-width: 0;
}

.channels-filters::v-deep .search-input {
  width: 100%;
}

.channels-filters::v-deep .sort-option-selected {
  min-width: 150px;
}

.channels-filters::v-deep .sort-option-selected .font-weight-bold {
  min-width: 0 !important;
  flex-direction: row;
}

.sidebar-header {
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  color: #888;
  margin-bottom: 10px;
}

.channel-group-title {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin: 8px 0px 4px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: background-color 120ms ease, color 120ms ease;

  &:hover {
    background-color: #f6f6f6;
  }

  &:active {
    background-color: #eceff3;
  }

  &.active {
    background-color: rgba($blue, 0.08);
    color: $blue;
  }
}

.channel-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background-color: #f6f6f6;
  }

  &.active {
    background-color: rgba($blue, 0.1);
    color: $blue;
  }
}

.posts-content {
  padding-left: 20px;
  overflow-y: auto;
  min-width: 0;
}

.post-card {
  border-radius: 10px;
  border: 1px solid #eee;

  &:hover {
    box-shadow: 0 0 5px rgba(100, 100, 100, 0.15);
  }
}

.post-title {
  font-weight: 600;
  font-size: 16px;
}

.post-body {
  font-size: 14px;
  color: #555;
  overflow-wrap: break-word;
  word-break: break-word;
  overflow: hidden;

  ::v-deep {
    p {
      margin: 0 0 0.4em;
    }

    h1 { font-size: 1.8em; font-weight: 700; }
    h2 { font-size: 1.4em; font-weight: 600; }
    h3 { font-size: 1.15em; font-weight: 600; }

    ul, ol {
      padding-left: 1.5em;
    }

    blockquote {
      border-left: 3px solid #ddd;
      padding-left: 12px;
      color: #666;
    }

    a {
      color: $blue;
      text-decoration: underline;
    }

    img {
      max-width: 100%;
      border-radius: 6px;
    }
  }
}

.post-date {
  font-size: 12px;
  color: #999;
}

.post-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.post-channel-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f1f3f5;
  color: #606770;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
}

.post-view-stats {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #8d8d8d;
  font-size: 11px;
  font-weight: 600;
  cursor: default;
}

.post-view-stat {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.post-thumbnail {
  width: 56px;
  height: 56px;
  min-width: 56px;
  border-radius: 8px;
  object-fit: cover;
}

.post-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.gallery-item {
  display: block;
  border-radius: 8px;
  overflow: hidden;
  line-height: 0;

  &:hover .gallery-image {
    transform: scale(1.03);
  }
}

.gallery-image {
  height: 140px;
  max-width: 260px;
  object-fit: cover;
  border-radius: 8px;
  transition: transform 150ms ease;
}

.post-files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.file-attachment {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f2f3f5;
  color: #555;
  font-size: 18px;
  transition: background 150ms, color 150ms;

  &:hover {
    background: #e4e6ea;
    color: $blue;
    text-decoration: none;
  }
}
</style>
